import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { type Expr, Polynomial, Fraction, parsePolynomial, format, factor, num } from '../algebra';
import type { AlgebraContext, AlgebraKey } from '../types';
import { createTermGrid } from '../render/term-grid';
import { createSvgEl } from '@learnforge/demo-render';
import { mountActionBar, type ActionDef } from '../action-bar';
import { POLYARITH_PRESETS } from '../data/polyarith-presets';

// ============================================================================
// Helper Types
// ============================================================================

export interface LongDivIteration {
  term: Polynomial; // the quotient term found this iteration
  product: Polynomial; // term · divisor
  remainder: Polynomial; // running remainder after subtraction
}

export interface SyntheticTable {
  coeffs: readonly Fraction[]; // dividend coefficients, DESCENDING
  products: readonly Fraction[]; // root·carry per column; products[0] = Fraction.ZERO placeholder
  results: readonly Fraction[]; // bottom row; last entry is the remainder
}

export interface SyntheticStep {
  description: string;
  row: readonly Fraction[];
}

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Multiply two polynomials and return both the result and the intermediate grid.
 * grid[i][j] = term a_i * b_j as an Expr; result = a.mul(b)
 */
export function multiplyPolynomialsWithGrid(
  a: Polynomial,
  b: Polynomial,
): { result: Polynomial; grid: Expr[][] } {
  const result = a.mul(b);

  // Build grid of products
  const grid: Expr[][] = [];
  for (let i = 0; i < a.coeffs.length; i += 1) {
    const row: Expr[] = [];
    for (let j = 0; j < b.coeffs.length; j += 1) {
      const prod = a.coeffs[i]!.mul(b.coeffs[j]!);
      const degree = i + j;
      let term: Expr;
      if (degree === 0) {
        term = { kind: 'num', value: prod };
      } else if (degree === 1) {
        term = {
          kind: 'mul',
          children: [
            { kind: 'num', value: prod },
            { kind: 'var', name: 'x' },
          ],
        };
      } else {
        term = {
          kind: 'mul',
          children: [
            { kind: 'num', value: prod },
            {
              kind: 'pow',
              base: { kind: 'var', name: 'x' },
              exp: { kind: 'num', value: new Fraction(degree, 1) },
            },
          ],
        };
      }
      row.push(term);
    }
    grid.push(row);
  }

  return { result, grid };
}

/**
 * Build one Expr term `coeff·x^degree` (monomial), in the same style as the
 * grid-cell construction in multiplyPolynomialsWithGrid above.
 */
function monomialAtDegree(coeff: Fraction, degree: number): Expr {
  if (degree === 0 || coeff.num === 0) {
    // A zero coefficient collapses to the bare number "0" regardless of
    // degree — without this, format() renders it literally as "0x"/"0x²"
    // (it never simplifies away a zero coefficient on its own).
    return { kind: 'num', value: coeff };
  } else if (degree === 1) {
    return {
      kind: 'mul',
      children: [
        { kind: 'num', value: coeff },
        { kind: 'var', name: 'x' },
      ],
    };
  }
  return {
    kind: 'mul',
    children: [
      { kind: 'num', value: coeff },
      {
        kind: 'pow',
        base: { kind: 'var', name: 'x' },
        exp: { kind: 'num', value: new Fraction(degree, 1) },
      },
    ],
  };
}

/**
 * Collect the like-term contributions of a·b by degree, for narrating the
 * "collect like terms" step of multiplication. Only degrees with two or more
 * nonzero contributions are returned — a single contribution needs no collecting.
 */
export function collectByDegree(
  a: Polynomial,
  b: Polynomial,
): { degree: number; contributions: string[]; sum: string }[] {
  const rows: { degree: number; contributions: string[]; sum: string }[] = [];
  const maxDegree = a.coeffs.length + b.coeffs.length - 2;

  for (let d = 0; d <= maxDegree; d += 1) {
    const contributions: string[] = [];
    let sum = Fraction.ZERO;

    for (let i = 0; i < a.coeffs.length; i += 1) {
      const j = d - i;
      if (j < 0 || j >= b.coeffs.length) continue;

      const prod = a.coeffs[i]!.mul(b.coeffs[j]!);
      if (prod.num === 0) continue;

      contributions.push(format(monomialAtDegree(prod, d), { useUnicodeSuperscripts: true }));
      sum = sum.add(prod);
    }

    if (contributions.length >= 2) {
      rows.push({
        degree: d,
        contributions,
        sum: format(monomialAtDegree(sum, d), { useUnicodeSuperscripts: true }),
      });
    }
  }

  return rows;
}

/**
 * "x", "x²", "x³", … — the bare variable part of a degree, for the collection
 * line's `{degreeLabel}: ...` prefix. Reuses the superscript digits also used
 * for GCD's factor multiplicities.
 */
function degreeLabel(degree: number): string {
  return degree === 1 ? 'x' : `x${toSuperscript(degree)}`;
}

/**
 * Join already-formatted, signed term strings with " + "/" - " the way
 * format()'s own `add` case does, so a negative contribution reads as
 * "6x - x" rather than "6x + -x".
 */
function joinSignedTerms(terms: readonly string[]): string {
  return terms
    .map((term, i) => {
      if (i === 0) return term;
      return term.startsWith('-') ? ` - ${term.slice(1)}` : ` + ${term}`;
    })
    .join('');
}

/**
 * Long division of dividend by divisor, recording every iteration of the
 * classic "divide leading terms, multiply back, subtract" loop so the stage
 * can render a numbered work-down list rather than just the final result.
 */
export function longDivideWithSteps(
  dividend: Polynomial,
  divisor: Polynomial,
): { quotient: Polynomial; remainder: Polynomial; steps: LongDivIteration[] } {
  if (divisor.isZero) {
    throw new Error('longDivideWithSteps: division by zero polynomial');
  }

  const steps: LongDivIteration[] = [];
  let remainder = new Polynomial(Array.from(dividend.coeffs), dividend.varName);
  let quotient = Polynomial.zero(dividend.varName);

  while (!remainder.isZero && remainder.degree >= divisor.degree) {
    const coeff = remainder.leadingCoefficient.div(divisor.leadingCoefficient);
    const degDiff = remainder.degree - divisor.degree;

    const termCoeffs: Fraction[] = Array.from({ length: degDiff + 1 }, () => Fraction.ZERO);
    termCoeffs[degDiff] = coeff;
    const term = new Polynomial(termCoeffs, dividend.varName);

    const product = divisor.mul(term);
    remainder = remainder.sub(product);
    quotient = quotient.add(term);

    steps.push({ term, product, remainder });
  }

  return { quotient, remainder, steps };
}

/**
 * The single leading-term monomial of `p` (e.g. leadingTerm(x³ + 2x² − 5x − 6) → x³).
 * Used to narrate long division's "lead ÷ lead → term" step.
 */
function leadingTerm(p: Polynomial): Polynomial {
  if (p.isZero) {
    return p;
  }
  const coeffs: Fraction[] = Array.from({ length: p.degree + 1 }, () => Fraction.ZERO);
  coeffs[p.degree] = p.leadingCoefficient;
  return new Polynomial(coeffs, p.varName);
}

/**
 * Synthetic division of polynomial by (x - root).
 * Returns quotient, remainder, and steps.
 */
export function syntheticDivide(
  dividend: Polynomial,
  root: Fraction,
): { quotient: Polynomial; remainder: Fraction; steps: SyntheticStep[] } {
  const steps: SyntheticStep[] = [];

  // Divisor is (x - root), so we use root value in synthetic division
  const coeffs = Array.from(dividend.coeffs);

  // Reverse so we can work from high degree to low
  coeffs.reverse();

  const resultCoeffs: Fraction[] = [];
  let carry = Fraction.ZERO;

  for (let i = 0; i < coeffs.length; i += 1) {
    const current = i === 0 ? coeffs[i]! : coeffs[i]!.add(carry.mul(root));
    if (i < coeffs.length - 1) {
      resultCoeffs.push(current);
    }
    carry = current;

    steps.push({
      description: `Step ${i + 1}: ${format({ kind: 'num', value: current })}`,
      row: [...resultCoeffs, carry],
    });
  }

  // Remainder is the final carry value; quotient is the rest, reversed back
  resultCoeffs.reverse();
  const quotient = new Polynomial(resultCoeffs, dividend.varName);
  const remainder = carry;

  return {
    quotient,
    remainder,
    steps,
  };
}

/**
 * Build the classic three-row synthetic-division table for `dividend ÷ (x − root)`:
 * descending coefficients on top, the root·carry products in the middle, and the
 * running sum (quotient coefficients, remainder last) on the bottom.
 */
export function syntheticTable(dividend: Polynomial, root: Fraction): SyntheticTable {
  const coeffs = Array.from(dividend.coeffs).reverse(); // descending

  const products: Fraction[] = [];
  const results: Fraction[] = [];

  for (let i = 0; i < coeffs.length; i += 1) {
    const product = i === 0 ? Fraction.ZERO : root.mul(results[i - 1]!);
    products.push(product);
    results.push(coeffs[i]!.add(product));
  }

  return { coeffs, products, results };
}

/**
 * GCD of two polynomials via Euclidean algorithm.
 */
export function polyGcd(a: Polynomial, b: Polynomial): Polynomial {
  return a.gcd(b);
}

/**
 * Verify remainder theorem: (p.divmod(x - root).remainder) equals p.evaluate(root)
 */
export function remainderTheoremCheck(p: Polynomial, root: Fraction): boolean {
  const divisor = Polynomial.linearFromRoot(root, p.varName);
  const { remainder } = p.divmod(divisor);

  // remainder should be a constant polynomial
  if (remainder.coeffs.length === 0) {
    // Zero polynomial
    const evaluated = p.evaluate(root);
    return evaluated.num === 0;
  }

  if (remainder.coeffs.length === 1) {
    // Constant
    const evaluated = p.evaluate(root);
    return remainder.coeffs[0]!.equals(evaluated);
  }

  // remainder should not be higher than constant for this check
  return false;
}

/**
 * Format a polynomial with unicode superscripts, wrapped in `(…)` when it
 * has two or more nonzero terms (so it reads unambiguously as a single
 * factor next to a `·` or `÷`). Monomials are left unbracketed.
 */
export function bracketedPolynomial(p: Polynomial): string {
  const s = format(p.toExpr(), { useUnicodeSuperscripts: true });
  const terms = p.coeffs.filter((c) => c.num !== 0).length;
  return terms > 1 ? `(${s})` : s;
}

// ============================================================================
// GCD factorisation display
// ============================================================================

const SUPERSCRIPT_DIGITS: { [key: string]: string } = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

function toSuperscript(n: number): string {
  return String(n)
    .split('')
    .map((ch) => SUPERSCRIPT_DIGITS[ch] ?? ch)
    .join('');
}

export interface FactorPart {
  text: string; // e.g. "(x + 1)" or a leading constant "2"
  common: boolean; // true → rendered as an accent tspan
}

/**
 * How many times (up to `capacity`) `monicFactor` divides `gcd` exactly.
 * Repeatedly divides out the factor so a group's multiplicity that is only
 * partially shared with the gcd (e.g. (x+1)^3 in P but only (x+1)^2 in the
 * gcd) reports the shared count rather than an all-or-nothing flag.
 */
function commonMultiplicityWithGcd(
  monicFactor: Polynomial,
  gcd: Polynomial,
  capacity: number,
): number {
  let count = 0;
  let remaining = gcd;

  while (count < capacity) {
    const { quotient, remainder } = remaining.divmod(monicFactor);
    if (!remainder.isZero) break;
    remaining = quotient;
    count += 1;
  }

  return count;
}

/**
 * Turn one polynomial's FactorResult into display parts: a leading-constant
 * part (never common) when the content isn't 1, then one part per factor
 * group — split into a common part and a not-common part when the gcd only
 * shares part of that group's multiplicity.
 */
function factorPartsForPolynomial(res: ReturnType<typeof factor>, gcd: Polynomial): FactorPart[] {
  const parts: FactorPart[] = [];

  if (!res.content.equals(Fraction.ONE)) {
    parts.push({
      text: format(num(res.content), { useUnicodeSuperscripts: true }),
      common: false,
    });
  }

  for (const group of res.factors) {
    const factorStr = format(group.factor.toExpr(), { useUnicodeSuperscripts: true });

    // Per contract, a factor can only be common when its degree >= 1 — every
    // entry in factor()'s `factors` array already satisfies this, but guard
    // defensively rather than assume it.
    const commonCount =
      group.factor.degree >= 1
        ? commonMultiplicityWithGcd(group.factor.toMonic().monic, gcd, group.multiplicity)
        : 0;

    if (commonCount > 0) {
      const exponent = commonCount > 1 ? toSuperscript(commonCount) : '';
      parts.push({ text: `(${factorStr})${exponent}`, common: true });
    }

    const remainingCount = group.multiplicity - commonCount;
    if (remainingCount > 0) {
      const exponent = remainingCount > 1 ? toSuperscript(remainingCount) : '';
      parts.push({ text: `(${factorStr})${exponent}`, common: false });
    }
  }

  return parts;
}

/**
 * Factorise both P(x) and Q(x) and flag the factors they share with
 * gcd(P, Q) so the GCD stage can render both factorisations with the common
 * part(s) in accent colour.
 */
export function gcdFactorisationParts(
  p: Polynomial,
  q: Polynomial,
): { pParts: FactorPart[]; qParts: FactorPart[]; gcd: Polynomial } {
  const gcd = polyGcd(p, q);

  return {
    pParts: factorPartsForPolynomial(factor(p), gcd),
    qParts: factorPartsForPolynomial(factor(q), gcd),
    gcd,
  };
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  mode: 'multiply' | 'long-divide' | 'synthetic' | 'gcd';
  polyA: string;
  polyB: string;
  polyRoot: string;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.polynomial-arithmetic.label',
    demoId: 'algebra',
    advancedKey: 'polynomial-arithmetic',

    initialState(): State {
      const defaultPreset = POLYARITH_PRESETS[0]!;
      return {
        presetId: defaultPreset.id,
        mode: defaultPreset.mode,
        polyA: defaultPreset.a,
        polyB: defaultPreset.b ?? '',
        polyRoot: defaultPreset.root ?? '1',
      };
    },

    applyPreset(state, presetId): State {
      const preset = POLYARITH_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return {
        ...state,
        presetId: preset.id,
        mode: preset.mode,
        polyA: preset.a,
        polyB: preset.b ?? '',
        polyRoot: preset.root ?? '1',
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', {
        viewBox: '0 0 800 460',
        class: 'alg-polynomial-arithmetic-svg',
      });
      host.appendChild(svg);

      const stageGroup = createSvgEl('g');
      svg.appendChild(stageGroup);

      // termGrid closure handle — multiply mode only
      let termGrid: ReturnType<typeof createTermGrid> | null = null;

      // Tracks the mode the action bar was last built for, so it only
      // rebuilds (and the swap button only appears/disappears) on a change.
      let lastMode: State['mode'] | undefined;

      // ── DOM: Panel ──

      // Mode dropdown
      const modeRow = document.createElement('div');
      modeRow.className = 'alg-panel-row alg-dropdown-row';
      const modeLabel = document.createElement('label');
      modeLabel.textContent = 'Mode:';
      const modeSelect = document.createElement('select');
      modeSelect.className = 'alg-select';

      const multiplyOpt = document.createElement('option');
      multiplyOpt.value = 'multiply';
      multiplyOpt.textContent = 'Multiply';
      modeSelect.appendChild(multiplyOpt);

      const longDivideOpt = document.createElement('option');
      longDivideOpt.value = 'long-divide';
      longDivideOpt.textContent = 'Long Divide';
      modeSelect.appendChild(longDivideOpt);

      const syntheticOpt = document.createElement('option');
      syntheticOpt.value = 'synthetic';
      syntheticOpt.textContent = 'Synthetic Division';
      modeSelect.appendChild(syntheticOpt);

      const gcdOpt = document.createElement('option');
      gcdOpt.value = 'gcd';
      gcdOpt.textContent = 'GCD';
      modeSelect.appendChild(gcdOpt);

      modeSelect.addEventListener('change', () => {
        controls.setState((s) => ({
          ...s,
          mode: modeSelect.value as State['mode'],
        }));
      });

      modeRow.appendChild(modeLabel);
      modeRow.appendChild(modeSelect);
      ctx.panel.appendChild(modeRow);

      // P(x) input
      const polyARow = document.createElement('div');
      polyARow.className = 'alg-panel-row';
      const polyALabel = document.createElement('label');
      polyALabel.textContent = 'P(x):';
      const polyAInput = document.createElement('input');
      polyAInput.type = 'text';
      polyAInput.className = 'alg-text-input';
      polyAInput.addEventListener('change', () => {
        try {
          // Validate parse succeeds before committing
          parsePolynomial(polyAInput.value, 'x');
          controls.setState((s) => ({ ...s, polyA: polyAInput.value }));
        } catch {
          // Revert to current state value (update() will sync the input back)
          controls.setState((s) => s);
        }
      });
      polyARow.appendChild(polyALabel);
      polyARow.appendChild(polyAInput);
      ctx.panel.appendChild(polyARow);

      // Q(x) / Root input (label toggles by mode)
      const polyBRow = document.createElement('div');
      polyBRow.className = 'alg-panel-row';
      const polyBLabel = document.createElement('label');
      const polyBInput = document.createElement('input');
      polyBInput.type = 'text';
      polyBInput.className = 'alg-text-input';
      polyBInput.addEventListener('change', () => {
        controls.setState((s) => {
          if (s.mode === 'synthetic') {
            try {
              Fraction.parse(polyBInput.value);
              return { ...s, polyRoot: polyBInput.value };
            } catch {
              return s;
            }
          } else {
            try {
              parsePolynomial(polyBInput.value, 'x');
              return { ...s, polyB: polyBInput.value };
            } catch {
              return s;
            }
          }
        });
      });
      polyBRow.appendChild(polyBLabel);
      polyBRow.appendChild(polyBInput);
      ctx.panel.appendChild(polyBRow);

      // ── DOM: Action bar — all presets are always available (no advanced gating) ──
      // Actions are (re)built in update() when mode changes, since the swap
      // button only appears for two-polynomial modes.
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);

      // ── updateStage: rebuild stageGroup contents from derived parsed values ──
      function updateStage(
        mode: State['mode'],
        polyA: Polynomial,
        polyB: Polynomial | null,
        polyRoot: Fraction | null,
      ): void {
        // Clear stage
        while (stageGroup.firstChild) {
          stageGroup.removeChild(stageGroup.firstChild);
        }
        // Destroy previous termGrid
        if (termGrid) {
          termGrid.destroy();
          termGrid = null;
        }

        try {
          if (mode === 'multiply' && polyB) {
            const { result } = multiplyPolynomialsWithGrid(polyA, polyB);

            const titleText = createSvgEl('text', {
              x: 50,
              y: 40,
              class: 'alg-text',
            });
            titleText.textContent = `${bracketedPolynomial(polyA)} · ${bracketedPolynomial(polyB)}`;
            stageGroup.appendChild(titleText);

            // Descending grid: highest power leftmost (columns) / topmost (rows)
            const rowLabels: Expr[] = [];
            for (let i = polyA.coeffs.length - 1; i >= 0; i -= 1) {
              const c = polyA.coeffs[i]!;
              rowLabels.push(
                i === 0
                  ? { kind: 'num', value: c }
                  : {
                      kind: 'mul',
                      children: [
                        { kind: 'num', value: c },
                        {
                          kind: 'pow',
                          base: { kind: 'var', name: 'x' },
                          exp: { kind: 'num', value: new Fraction(i, 1) },
                        },
                      ],
                    },
              );
            }

            const colLabels: Expr[] = [];
            for (let j = polyB.coeffs.length - 1; j >= 0; j -= 1) {
              const c = polyB.coeffs[j]!;
              colLabels.push(
                j === 0
                  ? { kind: 'num', value: c }
                  : {
                      kind: 'mul',
                      children: [
                        { kind: 'num', value: c },
                        {
                          kind: 'pow',
                          base: { kind: 'var', name: 'x' },
                          exp: { kind: 'num', value: new Fraction(j, 1) },
                        },
                      ],
                    },
              );
            }

            termGrid = createTermGrid(stageGroup, {
              rowLabels,
              colLabels,
              highlightByDegree: true,
              width: 600,
              height: 280,
            });
            termGrid.group.setAttribute('transform', 'translate(0, 56)');

            const resultText = createSvgEl('text', {
              x: 50,
              y: 380,
              class: 'alg-text',
            });
            resultText.textContent = `= ${format(result.toExpr(), { useUnicodeSuperscripts: true })}`;
            stageGroup.appendChild(resultText);

            // Collect-by-degree lines: narrate which grid cells combined into
            // each surviving term of the result (only degrees with >1 contributor).
            const collectionLineYs = [404, 424, 444];
            const collectionRows = collectByDegree(polyA, polyB);
            const shownRows = collectionRows.slice(0, collectionLineYs.length);
            const overflow = collectionRows.length - shownRows.length;
            // If there's overflow, the last visible line becomes a "+N more"
            // summary instead of a real row, so drop one real row to make room.
            const displayRows = overflow > 0 ? shownRows.slice(0, -1) : shownRows;

            displayRows.forEach((row, i) => {
              const lineText = createSvgEl('text', {
                x: 50,
                y: collectionLineYs[i]!,
                class: 'alg-text-mute',
              });
              lineText.textContent = `${degreeLabel(row.degree)}: ${joinSignedTerms(row.contributions)} = ${row.sum}`;
              stageGroup.appendChild(lineText);
            });

            if (overflow > 0) {
              const moreCount = collectionRows.length - displayRows.length;
              const moreText = createSvgEl('text', {
                x: 50,
                y: collectionLineYs[collectionLineYs.length - 1]!,
                class: 'alg-text-mute',
              });
              moreText.textContent = `+${moreCount} more`;
              stageGroup.appendChild(moreText);
            }
          } else if (mode === 'long-divide' && polyB) {
            const { quotient, remainder, steps } = longDivideWithSteps(polyA, polyB);

            const titleText = createSvgEl('text', {
              x: 50,
              y: 40,
              class: 'alg-text',
            });
            titleText.textContent = `${bracketedPolynomial(polyA)} ÷ ${bracketedPolynomial(polyB)}`;
            stageGroup.appendChild(titleText);

            const quotientText = createSvgEl('text', {
              x: 50,
              y: 80,
              class: 'alg-text',
            });
            quotientText.textContent = `Quotient: ${format(quotient.toExpr(), { useUnicodeSuperscripts: true })}`;
            stageGroup.appendChild(quotientText);

            const remainderText = createSvgEl('text', {
              x: 50,
              y: 104,
              class: 'alg-text',
            });
            remainderText.textContent = `Remainder: ${format(remainder.toExpr(), { useUnicodeSuperscripts: true })}`;
            stageGroup.appendChild(remainderText);

            // Numbered work-down list: one line per iteration of the division loop.
            let previousRemainder = polyA;
            steps.forEach((step, i) => {
              const dividendLead = bracketedPolynomial(leadingTerm(previousRemainder));
              const divisorLead = bracketedPolynomial(leadingTerm(polyB));
              const termStr = bracketedPolynomial(step.term);
              const stepRemainderStr = format(step.remainder.toExpr(), {
                useUnicodeSuperscripts: true,
              });

              const stepText = createSvgEl('text', {
                x: 50,
                y: 150 + i * 22,
                class: 'alg-text',
              });
              stepText.textContent = `${i + 1}.  ${dividendLead} ÷ ${divisorLead} → ${termStr}    −(${termStr}·${bracketedPolynomial(polyB)})    rem: ${stepRemainderStr}`;
              stageGroup.appendChild(stepText);

              previousRemainder = step.remainder;
            });
          } else if (mode === 'synthetic' && polyRoot !== null) {
            const { quotient, remainder } = syntheticDivide(polyA, polyRoot);
            const divisor = Polynomial.linearFromRoot(polyRoot, 'x');
            const table = syntheticTable(polyA, polyRoot);

            const titleText = createSvgEl('text', {
              x: 50,
              y: 40,
              class: 'alg-text',
            });
            titleText.textContent = `Synthetic division: ${bracketedPolynomial(polyA)} ÷ ${bracketedPolynomial(divisor)}`;
            stageGroup.appendChild(titleText);

            // Classic three-row table: coeffs / root·carry products / running results.
            const rootLabelText = createSvgEl('text', { x: 50, y: 150, class: 'alg-text' });
            rootLabelText.textContent = `c = ${polyRoot.toString()} |`;
            stageGroup.appendChild(rootLabelText);

            const colX = (i: number): number => 130 + i * 56;

            table.coeffs.forEach((c, i) => {
              const t = createSvgEl('text', {
                x: colX(i),
                y: 150,
                'text-anchor': 'middle',
                class: 'alg-text',
              });
              t.textContent = c.toString();
              stageGroup.appendChild(t);
            });

            table.products.forEach((p, i) => {
              // products[0] is a Fraction.ZERO placeholder — nothing is carried
              // into the first column, so leave that cell blank.
              const t = createSvgEl('text', {
                x: colX(i),
                y: 178,
                'text-anchor': 'middle',
                class: 'alg-text-mute',
              });
              t.textContent = i === 0 ? '' : p.toString();
              stageGroup.appendChild(t);
            });

            const ruleLine = createSvgEl('line', {
              x1: colX(0) - 28,
              y1: 190,
              x2: colX(table.coeffs.length - 1) + 28,
              y2: 190,
              class: 'alg-sketch-axis',
            });
            stageGroup.appendChild(ruleLine);

            // Box the remainder — the last cell of the results row — behind its
            // text (append the rect first so the text renders on top of it).
            const lastCol = table.results.length - 1;
            const remainderRect = createSvgEl('rect', {
              x: colX(lastCol) - 22,
              y: 191,
              width: 44,
              height: 26,
              class: 'alg-card',
            });
            stageGroup.appendChild(remainderRect);

            table.results.forEach((r, i) => {
              const t = createSvgEl('text', {
                x: colX(i),
                y: 210,
                'text-anchor': 'middle',
                class: 'alg-text',
              });
              t.textContent = r.toString();
              stageGroup.appendChild(t);
            });

            const quotientText = createSvgEl('text', {
              x: 50,
              y: 300,
              class: 'alg-text',
            });
            quotientText.textContent = `Quotient: ${format(quotient.toExpr(), { useUnicodeSuperscripts: true })}`;
            stageGroup.appendChild(quotientText);

            const remainderText = createSvgEl('text', {
              x: 50,
              y: 324,
              class: 'alg-text',
            });
            remainderText.textContent = `Remainder: ${format({ kind: 'num', value: remainder }, { useUnicodeSuperscripts: true })}`;
            stageGroup.appendChild(remainderText);
          } else if (mode === 'gcd' && polyB) {
            const { pParts, qParts, gcd } = gcdFactorisationParts(polyA, polyB);

            const titleText = createSvgEl('text', {
              x: 50,
              y: 40,
              class: 'alg-text',
            });
            titleText.textContent = `GCD of ${bracketedPolynomial(polyA)} and ${bracketedPolynomial(polyB)}`;
            stageGroup.appendChild(titleText);

            const appendFactorisationLine = (
              y: number,
              prefix: string,
              parts: FactorPart[],
            ): void => {
              const lineText = createSvgEl('text', { x: 50, y, class: 'alg-text' });
              const leading = createSvgEl('tspan');
              leading.textContent = prefix;
              lineText.appendChild(leading);
              for (const part of parts) {
                const tspan = createSvgEl(
                  'tspan',
                  part.common ? { class: 'alg-tspan-accent' } : {},
                );
                tspan.textContent = part.text;
                lineText.appendChild(tspan);
              }
              stageGroup.appendChild(lineText);
            };

            appendFactorisationLine(100, 'P(x) = ', pParts);
            appendFactorisationLine(132, 'Q(x) = ', qParts);

            const resultText = createSvgEl('text', {
              x: 50,
              y: 180,
              class: 'alg-text',
            });
            resultText.textContent = `GCD: ${format(gcd.toExpr(), { useUnicodeSuperscripts: true })}`;
            stageGroup.appendChild(resultText);
          }
        } catch (err) {
          const errorText = createSvgEl('text', {
            x: 50,
            y: 100,
            class: 'alg-text',
          });
          errorText.textContent = String(err);
          stageGroup.appendChild(errorText);
        }
      }

      // ── RenderHandle ──
      const update = (state: State): void => {
        const { mode, polyA: polyAStr, polyB: polyBStr, polyRoot: polyRootStr } = state;

        // --- Rebuild action bar when mode changes (swap only shows for two-polynomial modes) ---
        if (mode !== lastMode) {
          lastMode = mode;
          const actions: ActionDef[] = [
            {
              id: 'reset',
              labelKey: 'btn.reset',
              onClick: () => {
                controls.setState((s) => {
                  const preset = POLYARITH_PRESETS.find((p) => p.id === s.presetId)!;
                  return {
                    ...s,
                    mode: preset.mode,
                    polyA: preset.a,
                    polyB: preset.b ?? '',
                    polyRoot: preset.root ?? '1',
                  };
                });
              },
            },
            {
              id: 'randomise',
              labelKey: 'btn.randomise',
              onClick: () => {
                const randomPreset = POLYARITH_PRESETS[ctx.prng(POLYARITH_PRESETS.length)]!;
                controls.setState((s) => ({
                  ...s,
                  presetId: randomPreset.id,
                  mode: randomPreset.mode,
                  polyA: randomPreset.a,
                  polyB: randomPreset.b ?? '',
                  polyRoot: randomPreset.root ?? '1',
                }));
              },
            },
          ];
          if (mode !== 'synthetic') {
            actions.push({
              id: 'swap',
              labelKey: 'btn.swap',
              onClick: () => {
                controls.setState((s) => ({ ...s, polyA: s.polyB, polyB: s.polyA }));
              },
            });
          }
          actionBar.setActions(actions);
        }

        // --- Sync mode select ---
        modeSelect.value = mode;

        // --- Sync Q(x)/Root label ---
        polyBLabel.textContent = mode === 'synthetic' ? 'Root (c):' : 'Q(x):';

        // --- Sync P(x) input (guard caret) ---
        if (polyAInput.value !== polyAStr) {
          polyAInput.value = polyAStr;
        }

        // --- Sync Q(x)/Root input (guard caret) ---
        const polyBDisplayVal = mode === 'synthetic' ? polyRootStr : polyBStr;
        if (polyBInput.value !== polyBDisplayVal) {
          polyBInput.value = polyBDisplayVal;
        }

        // --- Parse and rebuild stage ---
        let parsedA: Polynomial;
        let parsedB: Polynomial | null = null;
        let parsedRoot: Fraction | null = null;
        try {
          parsedA = parsePolynomial(polyAStr, 'x');
        } catch {
          return;
        }
        if (mode !== 'synthetic') {
          try {
            if (polyBStr) parsedB = parsePolynomial(polyBStr, 'x');
          } catch {
            // leave null
          }
        } else {
          try {
            parsedRoot = Fraction.parse(polyRootStr);
          } catch {
            // leave null
          }
        }

        updateStage(mode, parsedA, parsedB, parsedRoot);
      };

      return {
        update,
        dispose() {
          // GUIDANCE C: only destroy termGrid + clear actionBar; do NOT touch ctx containers or svg
          if (termGrid) {
            termGrid.destroy();
            termGrid = null;
          }
          actionBar.clear();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
